import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { products, pricingRules } from '@/data/products';
import { catalogFilterDefinitions, headwearTypeOptions, validHeadwearTypeKeys } from '@/data/catalogTaxonomy';
import { getCapPresentationViews, capImageSrc } from '@/data/capPresentation';
import HeadwearDiscovery from '@/components/HeadwearDiscovery';
import { startCatalogRotation } from '@/lib/catalogRotation';

const MotionLink = motion(Link);

const Catalog = () => {
  const { t, language } = useLanguage();
  const reduceMotion = useReducedMotion();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleFilters = useMemo(
    () => catalogFilterDefinitions.filter((filter) => !filter.hidden),
    []
  );

  const requestedFilter = searchParams.get('filter');
  const requestedHeadwearType = searchParams.get('headwearType');

  const defaultFilter = visibleFilters.some((filter) => filter.key === requestedFilter)
    ? requestedFilter
    : visibleFilters[0]?.key || catalogFilterDefinitions[0].key;
  const defaultHeadwearType = requestedHeadwearType && validHeadwearTypeKeys.includes(requestedHeadwearType)
    ? requestedHeadwearType
    : 'all';

  const [activeFilter, setActiveFilter] = useState(defaultFilter);
  const [activeHeadwearType, setActiveHeadwearType] = useState(defaultHeadwearType);

  useEffect(() => {
    setActiveFilter(defaultFilter);
  }, [defaultFilter]);

  useEffect(() => {
    setActiveHeadwearType(defaultHeadwearType);
  }, [defaultHeadwearType]);

  const formatILS = (v) =>
    new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      maximumFractionDigits: 0
    }).format(v);

  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => (a.appearance || 0) - (b.appearance || 0)),
    []
  );

  const headwearTypeCounts = useMemo(
    () => headwearTypeOptions.reduce((acc, option) => {
      acc[option.key] = sortedProducts.filter((product) => (
        product.search_tag?.includes('headwear') &&
        product.headwearType === option.key
      )).length;
      return acc;
    }, {}),
    [sortedProducts]
  );

  const filteredProducts = useMemo(() => {
    if (activeFilter === 'all') return sortedProducts;
    if (activeFilter === 'headwear') {
      return sortedProducts.filter((product) => {
        if (!product.search_tag?.includes('headwear')) return false;
        if (!validHeadwearTypeKeys.includes(product.headwearType)) return false;
        if (activeHeadwearType === 'all') return true;
        return product.headwearType === activeHeadwearType;
      });
    }
    return sortedProducts.filter((product) => {
      const tags = Array.isArray(product.search_tag) ? product.search_tag : ['all'];
      return tags.includes(activeFilter);
    });
  }, [activeFilter, activeHeadwearType, sortedProducts]);

  const setCatalogFilter = (nextFilter) => {
    setActiveFilter(nextFilter);

    if (nextFilter !== 'headwear') {
      setActiveHeadwearType('all');
      setSearchParams(nextFilter === 'all' ? {} : { filter: nextFilter });
      return;
    }

    const nextParams = { filter: 'headwear' };
    if (activeHeadwearType !== 'all') {
      nextParams.headwearType = activeHeadwearType;
    }
    setSearchParams(nextParams);
  };

  const setHeadwearTypeFilter = (nextType) => {
    setActiveFilter('headwear');
    setActiveHeadwearType(nextType);

    const nextParams = { filter: 'headwear' };
    if (nextType !== 'all') {
      nextParams.headwearType = nextType;
    }
    setSearchParams(nextParams);
  };

  const emptyStateCopy = activeFilter === 'headwear' && activeHeadwearType !== 'all'
    ? {
        title: language === 'he' ? 'עדיין אין מוצרים משויכים לסוג הזה' : 'No products are mapped to this type yet',
        description: language === 'he'
          ? 'הקטגוריה קיימת, אבל עדיין לא הוזנו מוצרים בקטלוג עבור סוג כיסוי הראש שנבחר.'
          : 'The category exists, but no catalog products have been entered for the selected headwear type yet.'
      }
    : {
        title: language === 'he' ? 'לא נמצאו מוצרים' : 'No products found',
        description: language === 'he'
          ? 'אין כרגע מוצרים מתאימים למסנן שנבחר.'
          : 'There are currently no products matching the selected filter.'
      };

  return (
    <>
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('catalogTitle')}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {language === 'he'
                ? 'בחרו מוצר, צבע ומידה מתוך האפשרויות המוצגות בכל דף מוצר.'
                : 'Choose a product, color and size from the options shown on each product page.'}
            </p>
          </motion.div>

          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            {visibleFilters.map((filter) => {
              const isActive = filter.key === activeFilter;
              return (
                <button
                  key={filter.key}
                  type="button"
                  className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 active:translate-y-px motion-reduce:transform-none ${
                    isActive
                      ? 'bg-blue-600 text-white border-blue-600 shadow'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                  }`}
                  onClick={() => setCatalogFilter(filter.key)}
                  aria-pressed={isActive}
                >
                  {language === 'he' ? filter.labelHe : filter.labelEn}
                </button>
              );
            })}
          </div>

          {activeFilter === 'headwear' && (
            <div className="mb-10 rounded-2xl border border-blue-100 bg-blue-50/70 p-5">
              <div className="mb-4 text-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {language === 'he' ? 'סוגי כובעים' : 'Headwear Types In This Category'}
                </h2>
                
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 active:translate-y-px motion-reduce:transform-none ${
                    activeHeadwearType === 'all'
                      ? 'bg-blue-600 text-white border-blue-600 shadow'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                  }`}
                  onClick={() => setHeadwearTypeFilter('all')}
                  aria-pressed={activeHeadwearType === 'all'}
                >
                  {language === 'he' ? 'כל סוגי כובעים' : 'All Headwear Types'}
                </button>

                {headwearTypeOptions.map((option) => {
                  const isActive = activeHeadwearType === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 active:translate-y-px motion-reduce:transform-none ${
                        isActive
                          ? 'bg-blue-600 text-white border-blue-600 shadow'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                      }`}
                      onClick={() => setHeadwearTypeFilter(option.key)}
                      aria-pressed={isActive}
                    >
                      {language === 'he' ? option.labelHe : option.labelEn}
                      <span className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs ${isActive ? 'bg-white/20' : 'bg-blue-50 text-blue-700'}`}>
                        {headwearTypeCounts[option.key] || 0}
                      </span>
                    </button>
                  );
                })}
              </div>

            </div>
          )}

          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {filteredProducts.map((product, index) => (
                <MotionLink
                  key={product.sku}
                  to={`/product/${product.sku}`}
                  aria-label={`${language === 'he' ? 'לצפייה במוצר' : 'View product'} ${language === 'he' ? product.nameHe : product.name}`}
                  initial={reduceMotion ? false : { opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={reduceMotion ? { duration: 0 } : { duration: 0.7, delay: index * 0.1 }}
                  className="group flex flex-col overflow-hidden rounded-xl bg-white shadow-lg card-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500 focus-visible:ring-offset-4 active:bg-blue-50/40"
                  onMouseEnter={() => {
                    try { import(/* webpackPrefetch: true */ '@/pages/ProductConfigurator'); } catch {}
                  }}
                >
                  <div className="aspect-square overflow-hidden bg-gray-50 flex-shrink-0 pt-1 relative">
                    {(() => {
                      const { primary: primaryRaster, secondary: secondaryRaster, primaryAlt, secondaryAlt } = catalogImages(product, language);
                      const primaryBase = deriveBase(primaryRaster);
                      const secondaryBase = deriveBase(secondaryRaster);
                      const webpPrimary = primaryBase ? `${primaryBase}.webp` : null;
                      const webpSecondary = secondaryBase ? `${secondaryBase}.webp` : null;
                      const alt = language === 'he' ? product.nameHe : product.name;

                      if (!secondaryRaster) {
                        return <picture className="block w-full h-full">
                          {webpPrimary && <source type="image/webp" srcSet={webpPrimary} />}
                          <img src={primaryRaster} alt={primaryAlt || alt} className="w-full h-full object-cover object-top sm:object-contain" loading={index === 0 ? 'eager' : 'lazy'} fetchpriority={index === 0 ? 'high' : undefined} decoding="async" />
                        </picture>;
                      }

                      return (
                        <FlipTile
                          primaryRaster={primaryRaster}
                          webpPrimary={webpPrimary}
                          secondaryRaster={secondaryRaster}
                          webpSecondary={webpSecondary}
                          alt={alt}
                          primaryAlt={primaryAlt}
                          secondaryAlt={secondaryAlt}
                          prioritizePrimary={index === 0}
                        />
                      );
                    })()}
                  </div>

                  <div className="p-3 sm:p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {language === 'he' ? product.nameHe : product.name}
                    </h3>

                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-500">
                        {(language === 'he' ? 'צבעים' : 'colors')}:
                        <span className="inline-flex items-center ml-2 bg-blue-50 text-blue-700 text-sm font-medium px-2 py-0.5 rounded-full">
                          {product.colors.length}
                        </span>
                      </span>
                      <span className="text-sm sm:text-lg font-bold text-blue-600">
                        {(() => {
                          const tiers = pricingRules[product.sku]?.tiers || [];
                          const cheapestTier = tiers
                            .filter((tier) => Number.isFinite(Number(tier.price)))
                            .reduce((lowest, tier) => !lowest || Number(tier.price) < Number(lowest.price) ? tier : lowest, null);
                          if (!cheapestTier) return null;
                          const fromPrice = Number(cheapestTier.price);
                          return language === 'he'
                            ? `מחיר המוצר מ-${formatILS(fromPrice)} ליחידה`
                            : `Item from ${formatILS(fromPrice)} per unit`;
                        })()}
                      </span>
                    </div>

                    <div className="mt-auto">
                      <span className="inline-flex h-10 w-full items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors group-hover:bg-blue-700">
                        {language === 'he' ? 'בחר מוצר' : t('startDesigning')}
                      </span>
                    </div>
                  </div>
                </MotionLink>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
              <h3 className="text-2xl font-semibold text-gray-900">{emptyStateCopy.title}</h3>
              <p className="mx-auto mt-3 max-w-2xl text-gray-600">{emptyStateCopy.description}</p>
            </div>
          )}
        </div>
      </div>
      <HeadwearDiscovery />
    </>
  );
};

const pick = (arr, fallback) => {
  if (!Array.isArray(arr) || !arr.length) return fallback;
  return arr.find((x) => /(\.jpe?g|\.png)$/i.test(x)) || arr[0];
};

const rewriteLegacy = (src, sku) => {
  if (!src || typeof src !== 'string') return src;
  try {
    const m = src.match(/^(.*)\/base_(1|2)(\.[^.]+)?$/);
    if (m) {
      const ext = m[3] || '.png';
      return `/product_images/${sku}/base${m[2]}_${sku}${ext}`;
    }
  } catch {}
  return src;
};

const deriveBase = (src) => {
  if (!src || typeof src !== 'string') return null;
  const m = src.match(/(.+?)\.[^.]+$/);
  return m ? m[1] : null;
};

// Catalog presentation is separate from the colour/order image resolver.
const catalogImages = (product, language) => {
  const [primaryView, secondaryView] = getCapPresentationViews(product, language);
  return {
    primary: primaryView ? capImageSrc(primaryView.image) : rewriteLegacy(pick(product.images?.base1, `/product_images/${product.sku}/base1_${product.sku}.png`), product.sku),
    secondary: primaryView
      ? (secondaryView ? capImageSrc(secondaryView.image) : null)
      : rewriteLegacy(pick(product.images?.base2, `/product_images/${product.sku}/base2_${product.sku}.png`), product.sku),
    primaryAlt: primaryView?.alt,
    secondaryAlt: secondaryView?.alt,
  };
};

function FlipTile({ primaryRaster, webpPrimary, secondaryRaster, webpSecondary, alt, primaryAlt, secondaryAlt, prioritizePrimary }) {
  const [flipped, setFlipped] = useState(false);
  const tileRef = useRef(null);

  useEffect(() => startCatalogRotation({
    element: tileRef.current,
    onRotate: () => setFlipped((current) => !current),
  }), []);

  return (
    <div className="flip-3d" ref={tileRef}>
      <div className={`flip-3d-inner ${flipped ? 'is-flipped' : ''}`}>
        <div className="flip-face">
          <picture>
            {webpPrimary && <source type="image/webp" srcSet={webpPrimary} />}
            <img src={primaryRaster} alt={primaryAlt || alt} className="w-full h-full object-cover object-top sm:object-contain" loading={prioritizePrimary ? 'eager' : 'lazy'} fetchpriority={prioritizePrimary ? 'high' : undefined} decoding="async" />
          </picture>
        </div>
        <div className="flip-face flip-back">
          <picture>
            {webpSecondary && <source type="image/webp" srcSet={webpSecondary} />}
            <img src={secondaryRaster} alt={secondaryAlt || alt} className="w-full h-full object-cover object-top sm:object-contain" loading="lazy" decoding="async" />
          </picture>
        </div>
      </div>
    </div>
  );
}

export default Catalog;
